import type { ComponentType, ReactNode } from 'react';
import { isObject } from './common';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponentType = ComponentType<any>;

type ProviderConfig = {
  element: AnyComponentType;
  props?: Record<string, unknown>;
  key?: string;
};

type Provider = AnyComponentType | ProviderConfig;

type ProviderParsed = {
  Element: AnyComponentType;
  props: Record<string, unknown>;
};

function isProviderConfig(provider: Provider): provider is ProviderConfig {
  return isObject(provider) && 'element' in provider;
}

function getProviderKey(
  provider: Provider,
  Element: AnyComponentType,
): string | undefined {
  if (isProviderConfig(provider) && typeof provider.key === 'string') {
    return provider.key;
  }

  return Element.displayName ?? Element.name;
}

function parseProvider(provider: Provider): ProviderParsed {
  if (typeof provider === 'function') {
    return { Element: provider, props: {} };
  }

  if (isProviderConfig(provider)) {
    return {
      Element: provider.element,
      props: provider.props ?? {},
    };
  }

  throw new Error('Improperly configured.');
}

type ComposerProps = {
  children: ReactNode;
  providers?: Provider[];
};

function Composer({ children, providers = [] }: ComposerProps) {
  return (
    <>
      {providers.reduceRight((hierarchy, provider) => {
        const { Element, props: elementProps } = parseProvider(provider);
        const key = getProviderKey(provider, Element);
        return (
          <Element key={key} {...elementProps}>
            {hierarchy}
          </Element>
        );
      }, children)}
    </>
  );
}

Composer.displayName = 'Composer';
export default Composer;
